import React, { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import { Alert, Button, Dialog, DialogContent, Grid, IconButton, TextField, Typography, useTheme } from "@mui/material";
import DialogTitle from "../DialogTitle/DialogTitle";
import DialogActions from "../DialogActions/DialogActions";
import { PlusIcon } from "../../assets/icons";
import { RecipientProps as Props, RecipientState as State } from "./interfaces";
import { useTranslation } from "react-i18next";
import ConfirmationChangesDialog from "../ConfirmationChangesDialog/ConfirmationChangesDialog";
import { UsersApiService } from "../../services/users/users-api.service";
import { UserByEmailFilter } from "../../models/user-by-email/user-by-email.filter";
import { MailInboxAddreseeRecipientRef } from "src/models/mail-inbox-addressee-recipients";
import { UserDTO, UserRef, UserSTO } from "src/models/user";
import { USER_STATUS } from "src/utils/constants/constants";
import { UserStatusRef } from "src/models/user-status";
import { EMAIL_PATTERN, isValidString } from "src/utils/validation/isValidString";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { UserByEmailDTO } from "src/models/user-by-email";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";

const AddMailInboxAddresseeRecipient = ({ handleAddRecipient, buttonText, mailInboxAddressee }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const tRP = "components.add_edit_mail_inbox_addressee.add_mail_addressee_recipient";
  const usersTRPFields = "pages.system_settings.tables.users.fields";

  const DEFAULT_NEW_USER: UserSTO = useMemo(
    () => ({
      id: "",
      isDeleted: false,
      email: "",
      firstName: "",
      lastName: "",
      phone: "",
      status: USER_STATUS.active as UserStatusRef,
    }),
    []
  );

  const DEFAULT_RECIPIENT: MailInboxAddreseeRecipientRef = useMemo(
    () => ({
      id: "",
      isDeleted: false,
      mailInboxAddressee: mailInboxAddressee,
      user: null,
    }),
    [mailInboxAddressee]
  );

  const INITIAL_STATE: State = useMemo(
    () => ({
      open: false,
      CCDOpen: false,
      email: "",
      existingUser: null,
      loading: false,
      loadingSaveNewUser: false,
      error: "",
      userChecked: false,
      newUser: DEFAULT_NEW_USER,
    }),
    [DEFAULT_RECIPIENT]
  );

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const checkIfUserExists = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const service = new UsersApiService();
      service.useLoadItemsPath("/GetUserByEmail");
      const response = await service.loadItems({
        email: state.email,
      } as UserByEmailFilter);
      if (response.error) {
        setState((state) => ({
          ...state,
          error: response.error,
          existingUser: null,
        }));
      } else {
        setState((state) => ({
          ...state,
          existingUser: (response.items[0] as UserByEmailDTO).user,
        }));
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    } finally {
      setState((state) => ({
        ...state,
        loading: false,
        userChecked: true,
      }));
    }
  }, [state.email]);

  const handleConfirmNewRecipient = useCallback(
    (user: UserRef) => {
      handleAddRecipient({
        ...DEFAULT_RECIPIENT,
        id: uuid(),
        user: user, //converted to unkown to override the lastLoginAttemp
        mailInboxAddressee: mailInboxAddressee,
      });
      setState({ ...INITIAL_STATE });
    },
    [DEFAULT_RECIPIENT, INITIAL_STATE, handleAddRecipient, mailInboxAddressee]
  );

  const handleSaveNewUser = useCallback(async () => {
    setState((state) => ({ ...state, loadingSaveNewUser: true }));
    const newUser = {
      ...state.newUser,
      email: state.email,
      id: uuid(),
    } as UserSTO;

    try {
      const response = await new UsersApiService().saveItems([
        {
          ...newUser,
          firstName: newUser.firstName.trim(),
          lastName: newUser.lastName.trim(),
        },
      ]);
      if (response.error) {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      } else {
        handleConfirmNewRecipient(newUser as unknown as UserRef);
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: state.error,
      }));
    } finally {
      setState((state) => ({ ...state, loadingSaveNewUser: false }));
    }
  }, [state.newUser, state.email]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    timer = setTimeout(() => {
      if (isValidString(EMAIL_PATTERN, state.email)) {
        checkIfUserExists();
      }
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [state.email, checkIfUserExists]);

  return (
    <>
      {buttonText ? (
        <Button onClick={() => setState((state) => ({ ...state, open: true }))}>{buttonText}</Button>
      ) : (
        <IconButton
          onClick={() => setState((state) => ({ ...state, open: true }))}
          style={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            borderRadius: "5px",
            width: "25px",
            height: "25px",
          }}
        >
          <FontAwesomeIcon icon={faPlus} />
        </IconButton>
      )}

      <Dialog
        open={state.open}
        fullWidth
      >
        <DialogTitle
          title={`${t(`${tRP}.title`)}`}
          handleClose={() => setState((state) => ({ ...state, CCDOpen: true }))}
        />
        <DialogContent
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          <LoadingOverlay
            loading={state.loading || state.loadingSaveNewUser}
            error={state.error}
          >
            <Grid
              style={{
                paddingTop: "15px",
                display: "flex",
                gap: "10px",
                alignItems: "center",
                width: "100%",
              }}
            >
              <TextField
                value={state.email}
                onChange={({ currentTarget }) => setState((state) => ({ ...state, email: currentTarget.value.trim() }))}
                label={`${t(`${tRP}.recipient_email_address`)}`}
                error={!!state.email && !isValidString(EMAIL_PATTERN, state.email)}
                helperText={
                  !!state.email && !isValidString(EMAIL_PATTERN, state.email) && `${t(`${tRP}.invalid_mail`)}`
                }
                sx={{
                  width: "100%",
                }}
              />
              {state.loading && (
                <FontAwesomeIcon
                  icon={faRefresh}
                  spinPulse
                  color={theme.palette.primary.main}
                />
              )}
            </Grid>

            {/* <Alert severity="warning">{}</Alert> */}

            {state.userChecked && !state.existingUser && !state.error && (
              <Grid
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  marginTop: "10px",
                }}
              >
                <Alert severity="warning">{`${t(`${tRP}.user_does_not_exist`)}`}</Alert>
                <TextField
                  label={`${t(`${usersTRPFields}.first_name`)}`}
                  value={state.newUser?.firstName}
                  onChange={({ currentTarget }) =>
                    setState((state) => ({
                      ...state,
                      newUser: {
                        ...state.newUser,
                        firstName: currentTarget.value ?? "",
                      },
                    }))
                  }
                />
                <TextField
                  label={`${t(`${usersTRPFields}.last_name`)}`}
                  value={state.newUser?.lastName}
                  onChange={({ currentTarget }) =>
                    setState((state) => ({
                      ...state,
                      newUser: {
                        ...state.newUser,
                        lastName: currentTarget.value ?? "",
                      },
                    }))
                  }
                />
              </Grid>
            )}
          </LoadingOverlay>
        </DialogContent>
        <DialogActions
          handleCancel={() => setState((state) => ({ ...state, CCDOpen: true }))}
          handleSave={() =>
            state.userChecked && !state.existingUser
              ? handleSaveNewUser()
              : state.userChecked && state.existingUser && handleConfirmNewRecipient(state.existingUser)
          }
          customSaveName={`${t(`buttons.confirm`)}`}
          disabledSaveButton={
            (state.userChecked && !state.existingUser && (!state.newUser.firstName || !state.newUser.lastName)) ||
            state.loading ||
            state.loadingSaveNewUser ||
            // !state.existingUser &&
            !isValidString(EMAIL_PATTERN, state.email) ||
            (isValidString(EMAIL_PATTERN, state.email) && !state.userChecked)
          }
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() => setState({ ...INITIAL_STATE })}
      />
    </>
  );
};

export default AddMailInboxAddresseeRecipient;
